import { cn } from "@/lib/utils";
import { TONES, getInverseText, getTone } from "@/lib/ui/tones";

/**
 * Design-system status tones (outlined pill + tone dot). One source of truth for
 * every "status" chip — assignment status, enrollment progress, etc. These are
 * the *semantic* names; the class strings behind them live in `lib/ui/tones.js`,
 * so restyling a hue there updates every surface at once.
 */
export const STATUS_TONES = {
  neutral: TONES.gray,
  info: TONES.action,
  success: TONES.emerald,
  warning: TONES.warning,
  danger: TONES.rose,
  premium: TONES.premium,
};

/**
 * Resolve a tone name to its ramp. Accepts a semantic status ("success") or a
 * raw hue ("violet") so a categorical tag and a status chip share one lookup;
 * anything unrecognised falls back to neutral rather than rendering untoned.
 */
export function resolveTone(tone) {
  return Object.hasOwn(STATUS_TONES, tone) ? STATUS_TONES[tone] : getTone(tone);
}

/** Pill text treatments, shared with `Badge` (the dot-less chip). */
export const PILL_SIZES = {
  // §6 "Standard status pill".
  default: "px-2.5 py-0.5 text-xs font-medium",
  // The inline badge beside a list-row title (§5 "Grouped library list").
  sm: "px-2 py-0.5 text-[11px] font-medium",
  // §6 "Uppercase tag pill" — for card headers where it sits beside the
  // module tag and the mode badge, which are tags too.
  tag: "px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider whitespace-nowrap",
  // An inline tag inside a name row or another chip: a persona's language
  // beside its name, a label inside a picker chip.
  xs: "px-1.5 py-0 text-[10px] leading-[14px] font-medium",
  // §5 count chip beside a row title: "3 tests", "2 paths".
  count: "px-1.5 py-0.5 text-[10px] font-semibold tabular-nums",
  // A percentage read as a value, not a label (result cards, score columns).
  score: "px-2.5 py-1 text-sm font-semibold tabular-nums",
  // The pills at the right of a result or assignment header's eyebrow rail.
  header: "px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.12em] whitespace-nowrap",
};
const SIZES = PILL_SIZES;

/**
 * The flat treatment of the same tones — tone dot + toned text, no outline and
 * no fill — for a status that sits in a row of plain facts rather than on its
 * own. Mirrors `GradeBadge`, so a grade and a score read
 * identically wherever the studio's display mode decides between them.
 */
export const LABEL_SIZES = {
  default: "text-xs font-semibold",
  sm: "text-[11px] font-semibold",
  // The assignment card's header, where it sits in the card's 13px meta type.
  md: "text-[13px] font-medium",
  score: "text-xs font-semibold tabular-nums",
};

/**
 * Status pill with a leading tone dot.
 *
 * @param {string} [props.tone]  A semantic name from STATUS_TONES ("success",
 *   "danger", …) or a hue from `lib/ui/tones.js` ("emerald", "violet", …) for
 *   a categorical tag. Unknown names fall back to neutral.
 * @param {keyof typeof PILL_SIZES} [props.size]    Text treatment (default "default").
 * @param {"pill"|"soft"|"label"} [props.variant]  "pill" is outlined and tinted;
 *   "soft" is tinted with no outline (a result or assignment header, a buyer's
 *   attitude); "label" drops outline and fill, leaving the tone dot and toned
 *   text (see LABEL_SIZES).
 * @param {boolean} [props.inverse] The label variant on the navy surface
 *   (`bg-surface-inverse`): the text reads the tone's on-dark slot, the dot
 *   stays the tone's solid. The tinted variants have no on-dark version.
 * @param {import("react").ComponentType} [props.icon] Replaces the tone dot,
 *   for a pill that names a kind rather than a status ("Phone call").
 * @param {React.ReactNode} props.label             Pill text.
 * @param {string} [props.title]
 * @param {string} [props.className]
 */
export default function StatusPill({
  tone = "neutral",
  size = "default",
  variant = "pill",
  inverse = false,
  icon: Icon = null,
  label,
  title,
  className,
}) {
  const t = resolveTone(tone);
  const isLabel = variant === "label";
  const labelText = inverse ? t.inverse || getInverseText() : t.text;
  return (
    <span
      title={title}
      className={cn(
        "inline-flex items-center gap-1.5",
        isLabel
          ? cn("leading-none", LABEL_SIZES[size] || LABEL_SIZES.default, labelText)
          : cn(
              "rounded-full",
              SIZES[size] || SIZES.default,
              variant === "soft" ? cn(t.bg, t.text) : cn("border", t.pill),
            ),
        className,
      )}
    >
      {Icon ? (
        <Icon className="h-3 w-3 shrink-0" aria-hidden />
      ) : (
        <span className={cn("h-1.5 w-1.5 shrink-0 rounded-full", t.dot)} aria-hidden />
      )}
      {label}
    </span>
  );
}
